import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Configure axios
const api = axios.create({
    baseURL: `${API}/api`,
    withCredentials: true,
    headers: {
        'Content-Type': 'application/json'
    }
});

// Token refresh state
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
    failedQueue.forEach(prom => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve();
        }
    });
    failedQueue = [];
};

// Response interceptor for token refresh
api.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;
        
        if (error.response?.status === 401 && !originalRequest._retry) {
            if (isRefreshing) {
                // Wait for the refresh to complete
                return new Promise((resolve, reject) => {
                    failedQueue.push({ resolve, reject });
                }).then(() => {
                    return api(originalRequest);
                }).catch(err => {
                    return Promise.reject(err);
                });
            }
            
            originalRequest._retry = true;
            isRefreshing = true;
            
            try {
                await api.post('/auth/refresh');
                processQueue(null);
                return api(originalRequest);
            } catch (refreshError) {
                processQueue(refreshError);
                // Only redirect if we're not already on login page
                if (!window.location.pathname.includes('/login')) {
                    window.location.href = '/login';
                }
                return Promise.reject(refreshError);
            } finally {
                isRefreshing = false;
            }
        }
        
        return Promise.reject(error);
    }
);

// Format API error
export function formatApiError(error) {
    const detail = error.response?.data?.detail;
    if (detail == null) return "Si è verificato un errore. Riprova.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) {
        return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
    }
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
}

// Auth
export const authApi = {
    login: (email, password) => api.post('/auth/login', { email, password }),
    logout: () => api.post('/auth/logout'),
    me: () => api.get('/auth/me'),
    refresh: () => api.post('/auth/refresh')
};

// Users
export const usersApi = {
    getAll: () => api.get('/users'),
    create: (data) => api.post('/users', data),
    update: (id, data) => api.put(`/users/${id}`, data),
    delete: (id) => api.delete(`/users/${id}`),
    getMyProfile: () => api.get('/users/me/profile'),
    updateMyProfile: (data) => api.put('/users/me/profile', data),
};

// Roles
export const rolesApi = {
    getAll: () => api.get('/roles')
};

// Customers
export const customersApi = {
    getAll: (search) => api.get('/customers', { params: { search } }),
    getOne: (id) => api.get(`/customers/${id}`),
    create: (data) => api.post('/customers', data),
    update: (id, data) => api.put(`/customers/${id}`, data)
};

// Contracts
export const contractsApi = {
    getAll: (params) => api.get('/contracts', { params }),
    getOne: (id) => api.get(`/contracts/${id}`),
    create: (data) => api.post('/contracts', data),
    update: (id, data) => api.put(`/contracts/${id}`, data),
    updateBaseData: (id, data) => api.put(`/contracts/${id}/base-data`, data),
    getWorkflow: (id) => api.get(`/contracts/${id}/workflow`),
    getTimeline: (id) => api.get(`/contracts/${id}/timeline`),
    getDocuments: (id) => api.get(`/contracts/${id}/documents`)
};

// Contract Nodes
export const nodesApi = {
    getOne: (id) => api.get(`/contract-nodes/${id}`),
    start: (id) => api.post(`/contract-nodes/${id}/start`),
    complete: (id) => api.post(`/contract-nodes/${id}/complete`),
    approve: (id) => api.post(`/contract-nodes/${id}/approve`),
    reopen: (id) => api.post(`/contract-nodes/${id}/reopen`),
    updateStatus: (id, data) => api.put(`/contract-nodes/${id}/status`, data),
    updateChecklist: (id, data) => api.put(`/contract-nodes/${id}/checklist`, data),
    updateFormData: (id, formData) => api.put(`/contract-nodes/${id}/form-data`, { form_data: formData })
};

// Workflow Templates (legacy)
export const templatesApi = {
    getAll: (activeOnly) => api.get('/workflow-templates', { params: { active_only: activeOnly } }),
    getOne: (id) => api.get(`/workflow-templates/${id}`),
    create: (data) => api.post('/workflow-templates', data),
    update: (id, data) => api.put(`/workflow-templates/${id}`, data),
    delete: (id) => api.delete(`/workflow-templates/${id}`)
};

// ======================= ENTERPRISE APIs =======================

// Step Library
export const stepLibraryApi = {
    getAll: (params) => api.get('/step-library', { params }),
    getOne: (id) => api.get(`/step-library/${id}`),
    create: (data) => api.post('/step-library', data),
    update: (id, data) => api.put(`/step-library/${id}`, data),
    delete: (id) => api.delete(`/step-library/${id}`),
    getCategories: () => api.get('/step-library/categories/list')
};

// Products
export const productsApi = {
    getAll: (params) => api.get('/products', { params }),
    getOne: (id) => api.get(`/products/${id}`),
    create: (data) => api.post('/products', data),
    update: (id, data) => api.put(`/products/${id}`, data),
    delete: (id) => api.delete(`/products/${id}`)
};

// Product Workflows
export const productWorkflowsApi = {
    getAll: (productId) => api.get('/product-workflows', { params: { product_id: productId } }),
    getOne: (id) => api.get(`/product-workflows/${id}`),
    create: (data) => api.post('/product-workflows', data),
    update: (id, data) => api.put(`/product-workflows/${id}`, data),
    delete: (id) => api.delete(`/product-workflows/${id}`),
    duplicate: (id, data) => api.post(`/product-workflows/${id}/duplicate`, data)
};

// Migration
export const migrationApi = {
    migrateTemplatesToProducts: () => api.post('/migrate/templates-to-products')
};

// Catalog / Materials
export const catalogApi = {
    getItems: (params) => api.get('/catalog/items', { params }),
    getItem: (id) => api.get(`/catalog/items/${id}`),
    getBrands: (params) => api.get('/catalog/brands', { params }),
    getTypes: () => api.get('/catalog/types'),
};

// Documents
export const documentsApi = {
    upload: (formData) => api.post('/documents/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
    }),
    getOne: (id) => api.get(`/documents/${id}`),
    getVersions: (id) => api.get(`/documents/${id}/versions`),
    getPreviewUrl: (id) => api.get(`/documents/${id}/preview-url`),
    download: (id) => api.get(`/documents/${id}/download`, { responseType: 'blob' }),
    validate: (id, data) => api.post(`/documents/${id}/validate`, data),
    delete: (id) => api.delete(`/documents/${id}`),
    getByNode: (nodeId) => api.get(`/contract-nodes/${nodeId}/documents`),
    getByContract: (contractId) => api.get(`/contracts/${contractId}/documents`)
};

// Dashboard
export const dashboardApi = {
    getOverview: () => api.get('/dashboard/overview'),
    getKpi: () => api.get('/dashboard/kpi'),
    getBottlenecks: () => api.get('/dashboard/bottlenecks')
};

// Notifications
export const notificationsApi = {
    getAll: () => api.get('/notifications'),
    markRead: (ids) => api.post('/notifications/read', { notification_ids: ids })
};

// Audit Logs
export const auditApi = {
    getAll: (params) => api.get('/audit-logs', { params })
};

// Product Codes
export const productCodesApi = {
    getAll: () => api.get('/product-codes')
};

// ======================= QUOTES / PREVENTIVI =======================
export const quotesApi = {
    // CRUD
    getAll: (params) => api.get('/quotes', { params }),
    getById: (id) => api.get(`/quotes/${id}`),
    create: (data) => api.post('/quotes', data),
    update: (id, data) => api.put(`/quotes/${id}`, data),
    delete: (id) => api.delete(`/quotes/${id}`),
    
    // Lines
    updateLines: (id, lines) => api.put(`/quotes/${id}/lines`, { lines }),
    addFromCatalog: (id, catalogItemId, quantity = 1) => 
        api.post(`/quotes/${id}/lines/add-from-catalog?catalog_item_id=${catalogItemId}&quantity=${quantity}`),
    addService: (id, serviceId, quantity = 1) => 
        api.post(`/quotes/${id}/lines/add-service?service_catalog_id=${serviceId}&quantity=${quantity}`),
    
    // Single Line CPQ
    updateLine: (id, lineIndex, data) => api.put(`/quotes/${id}/lines/${lineIndex}`, data),
    deleteLine: (id, lineIndex) => api.delete(`/quotes/${id}/lines/${lineIndex}`),
    
    // Adjustments
    addAdjustment: (id, data) => api.post(`/quotes/${id}/adjustments`, data),
    removeAdjustment: (id, adjustmentId) => api.delete(`/quotes/${id}/adjustments/${adjustmentId}`),
    
    // Status
    updateStatus: (id, status, note) => api.put(`/quotes/${id}/status`, { status, note }),
    
    // Simulation
    updateSimulation: (id, data) => api.put(`/quotes/${id}/simulation`, data),
    calculateSimulation: (id) => api.post(`/quotes/${id}/simulation/calculate`),
    
    // Convert to Contract
    convertToContract: (id, data) => api.post(`/quotes/${id}/convert-to-contract`, data),
    
    // Statistics
    getStats: () => api.get('/quotes/stats/summary'),
    
    // Service Catalog
    getServiceCatalog: (params) => api.get('/quotes/services/catalog', { params }),
    createService: (data) => api.post('/quotes/services/catalog', data),
    updateService: (id, data) => api.put(`/quotes/services/catalog/${id}`, data),
    deleteService: (id) => api.delete(`/quotes/services/catalog/${id}`),
    
    // PDF Generation
    downloadPdf: async (id, filename, version = 'detailed') => {
        const response = await api.get(`/quotes/${id}/pdf?version=${version}`, { responseType: 'blob' });
        // Create download link
        const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename || `Preventivo_${id}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        return response;
    },
    
    // Bill Extraction
    extractBillData: (id, imageBase64, mimeType) => api.post(`/quotes/${id}/extract-bill`, {
        image_base64: imageBase64,
        mime_type: mimeType || 'image/jpeg'
    }),
    
    // Supply Data
    updateSupplyData: (id, data) => api.put(`/quotes/${id}/supply-data`, data)
};

// Store HVAC API
// Helper per scaricare blob come file
const downloadBlob = (data, filename) => {
    const url = window.URL.createObjectURL(new Blob([data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
};

export const storeApi = {
    // Products
    getProducts: (params) => api.get('/store/products', { params }),
    getProduct: (id) => api.get(`/store/products/${id}`),
    createProduct: (data) => api.post('/store/products', data),
    updateProduct: (id, data) => api.put(`/store/products/${id}`, data),
    deleteProduct: (id) => api.delete(`/store/products/${id}`),
    getCatalogStats: () => api.get('/store/stats/catalog'),
    getProductsMeta: () => api.get('/store/products/meta'),
    
    // PDF branding + versioning
    getPdfSettings: () => api.get('/store/pdf-settings'),
    updatePdfSettings: (data) => api.put('/store/pdf-settings', data),
    generateQuotePdf: (quoteId, variant = 'full') => api.post(`/store/quotes/${quoteId}/pdf/generate`, null, { params: { variant } }),
    getQuotePdfVersions: (quoteId) => api.get(`/store/quotes/${quoteId}/pdf/versions`),
    downloadQuotePdfVersion: async (quoteId, version, filename, variant = 'full') => {
        const response = await api.get(`/store/quotes/${quoteId}/pdf/versions/${version}`, { params: { variant }, responseType: 'blob' });
        downloadBlob(response.data, filename || `preventivo_v${version}.pdf`);
        return response;
    },
    
    // Bulk operations (Modifiche Rapide)
    getBulkOperations: () => api.get('/store/bulk/operations'),
    bulkPreview: (data) => api.post('/store/bulk/preview', data),
    bulkApply: (data) => api.post('/store/bulk/apply', data),
    
    // Audit log
    getAuditLog: (params) => api.get('/store/audit', { params }),
    
    // Excel import/export
    downloadExcelTemplate: async () => {
        const response = await api.get('/store/products/excel/template', { responseType: 'blob' });
        downloadBlob(response.data, `modello_prodotti_store_${new Date().toISOString().slice(0, 10)}.xlsx`);
        return response;
    },
    exportCatalog: async () => {
        const response = await api.get('/store/products/excel/export', { responseType: 'blob' });
        downloadBlob(response.data, `catalogo_store_${new Date().toISOString().slice(0, 10)}.xlsx`);
        return response;
    },
    previewExcelImport: (file) => {
        const formData = new FormData();
        formData.append('file', file);
        return api.post('/store/products/excel/preview', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
    },
    importExcel: (file) => {
        const formData = new FormData();
        formData.append('file', file);
        return api.post('/store/products/excel/import', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
    },
    
    // Promotions
    getPromotions: (params) => api.get('/store/promotions', { params }),
    getPromotion: (id) => api.get(`/store/promotions/${id}`),
    createPromotion: (data) => api.post('/store/promotions', data),
    updatePromotion: (id, data) => api.put(`/store/promotions/${id}`, data),
    deletePromotion: (id) => api.delete(`/store/promotions/${id}`),
    togglePromotion: (id) => api.post(`/store/promotions/${id}/toggle`),
    
    // Quotes
    getQuotes: (params) => api.get('/store/quotes', { params }),
    getQuote: (id) => api.get(`/store/quotes/${id}`),
    createQuote: (data) => api.post('/store/quotes', data),
    updateQuote: (id, data) => api.put(`/store/quotes/${id}`, data),
    addProductToQuote: (quoteId, productData) => api.post(`/store/quotes/${quoteId}/add-product`, productData),
    addServiceToQuote: (quoteId, serviceData) => api.post(`/store/quotes/${quoteId}/add-service`, serviceData),
    removeLineFromQuote: (quoteId, lineIndex) => api.delete(`/store/quotes/${quoteId}/lines/${lineIndex}`),
    updateQuoteLine: (quoteId, lineIndex, patch) => api.put(`/store/quotes/${quoteId}/lines/${lineIndex}`, patch),
    updateQuoteFinancing: (quoteId, financing) => api.put(`/store/quotes/${quoteId}/financing`, { financing }),
    addManualDiscount: (quoteId, data) => api.post(`/store/quotes/${quoteId}/add-discount`, data),
    deleteQuote: (quoteId) => api.delete(`/store/quotes/${quoteId}`),
    updateQuoteStatus: (id, status) => api.put(`/store/quotes/${id}/status?status=${status}`),
    convertQuoteToSale: (id, payload = {}) => api.post(`/store/quotes/${id}/convert`, payload),
    
    // PDF
    downloadQuotePdf: async (id, filename) => {
        const response = await api.get(`/store/quotes/${id}/pdf`, { responseType: 'blob' });
        const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename || `Proposta_Store_${id}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        return response;
    },
    
    // Statistics
    getQuotesStats: (params) => api.get('/store/quotes/stats/summary', { params })
};

export default api;
